// AdBanner Component for Google AdSense Display/Banner Ads
import React, { useEffect } from 'react';

// Declare adsbygoogle for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

// Ensure this file is treated as a module
export {};

interface AdBannerProps {
  adSlot: string; // Your AdSense ad slot ID
  adFormat?: 'auto' | 'horizontal' | 'vertical' | 'rectangle';
  fullWidthResponsive?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * AdBanner component for displaying Google AdSense ads
 *
 * Usage:
 * <AdBanner
 *   adSlot="1234567890"  // Replace with your actual ad slot ID
 *   adFormat="auto"
 *   fullWidthResponsive={true}
 * />
 *
 * Ad Slot Types:
 * - Display ads: General banner ads
 * - In-feed ads: Blend with content (forums, lists)
 * - In-article ads: Between paragraphs
 */
const AdBanner: React.FC<AdBannerProps> = ({
  adSlot,
  adFormat = 'auto',
  fullWidthResponsive = true,
  style = {},
  className = '',
}) => {
  useEffect(() => {
    try {
      // Push ad to AdSense queue
      if (typeof window !== 'undefined' && window.adsbygoogle && process.env.NODE_ENV === 'production') {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (error) {
      console.error('AdSense error:', error);
    }
  }, []);

  // Don't show ads in development mode
  if (process.env.NODE_ENV !== 'production') {
    return (
      <div
        className={`bg-gray-700 border-2 border-dashed border-gray-500 flex items-center justify-center ${className}`}
        style={{
          minHeight: '90px',
          ...style,
        }}
      >
        <p className="text-gray-400 text-sm">Ad Placeholder (Dev Mode)</p>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', ...style }}
        data-ad-client="ca-pub-6528149778805400"
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive.toString()}
      />
    </div>
  );
};

export default AdBanner;
